import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, User, Clock, AlertCircle, ArrowLeft, X, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMEZONE = 'America/Toronto'; // shop timezone — never the browser's

const formatWhen = (iso, language) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const locale = language === 'fr' ? 'fr-CA' : 'en-CA';
    const date = d.toLocaleDateString(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: TIMEZONE,
    });
    const time = d.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: TIMEZONE,
    });
    return `${date} · ${time}`;
  } catch {
    return iso;
  }
};

const formatSlotTime = (iso, language) =>
  new Date(iso).toLocaleTimeString(language === 'fr' ? 'fr-CA' : 'en-CA', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: TIMEZONE,
    hour12: language !== 'fr',
  });

const formatSlotDay = (isoDate, language) => {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(
    language === 'fr' ? 'fr-CA' : 'en-CA',
    { weekday: 'short', day: 'numeric', month: 'short' }
  );
};

// Montreal calendar date (YYYY-MM-DD) of a UTC slot timestamp
const toShopISODate = (utcIso) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(utcIso));
  const get = (t) => parts.find((p) => p.type === t)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
};

const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const ManageBookingPage = () => {
  const { t, language } = useLanguage();
  const m = t.booking.manage;
  const [params] = useSearchParams();
  const token = params.get('token');

  useSEO({ title: m.managePageTitle, description: m.intro });

  const [state, setState] = useState('loading'); // loading | ready | invalid
  const [data, setData] = useState(null);
  const [toast, setToast] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null); // booking

  // Reschedule modal state
  const [reschedTarget, setReschedTarget] = useState(null); // booking
  const [reschedSlots, setReschedSlots] = useState({}); // {YYYY-MM-DD: [slots]}
  const [reschedLoading, setReschedLoading] = useState(false);
  const [reschedDate, setReschedDate] = useState(null); // YYYY-MM-DD
  const [reschedSlot, setReschedSlot] = useState(null);
  const [reschedSubmitting, setReschedSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setState('invalid');
      return;
    }
    try {
      const { data: resp } = await axios.get(`${API}/bookings/manage`, {
        params: { token },
      });
      setData(resp);
      setState('ready');
    } catch (e) {
      setState('invalid');
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const openReschedule = async (booking) => {
    setReschedTarget(booking);
    setReschedSlots({});
    setReschedDate(null);
    setReschedSlot(null);
    setReschedLoading(true);
    try {
      const start = new Date();
      const end = new Date();
      end.setDate(end.getDate() + 30);
      const { data: slots } = await axios.post(`${API}/square/availability`, {
        service_variation_id: booking.service_variation_id,
        team_member_id: booking.team_member_id || null,
        start_date: toISODate(start),
        end_date: toISODate(end),
      });
      const bucketed = {};
      (slots || []).forEach((s) => {
        const key = toShopISODate(s.start_at);
        if (!bucketed[key]) bucketed[key] = [];
        bucketed[key].push(s);
      });
      setReschedSlots(bucketed);
      const first = Object.keys(bucketed).sort()[0];
      if (first) setReschedDate(first);
    } catch (e) {
      setReschedSlots({});
    } finally {
      setReschedLoading(false);
    }
  };

  const onReschedule = async () => {
    if (!reschedTarget || !reschedSlot || reschedSubmitting) return;
    setReschedSubmitting(true);
    try {
      await axios.post(`${API}/bookings/reschedule`, {
        token,
        booking_id: reschedTarget.booking_id,
        start_at: reschedSlot.start_at,
        lang: language,
      });
      setToast(m.rescheduled);
      setReschedTarget(null);
      await load();
    } catch (e) {
      setToast(e?.response?.data?.detail || m.errorGeneric);
    } finally {
      setReschedSubmitting(false);
    }
  };

  const onCancel = async (booking) => {
    try {
      await axios.post(`${API}/bookings/cancel`, {
        token,
        booking_id: booking.booking_id,
        lang: language,
      });
      setToast(m.cancelled);
      setCancelTarget(null);
      await load();
    } catch (e) {
      setToast(
        e?.response?.data?.detail || m.errorGeneric
      );
      setCancelTarget(null);
    }
  };

  // Invalid / expired token
  if (state === 'invalid') {
    return (
      <div className="min-h-screen bg-[#F5F0E8] pt-32 pb-24" data-testid="manage-invalid">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <AlertCircle size={32} className="mx-auto text-[#A14223] mb-6" />
          <h1 className="font-display text-3xl lg:text-4xl text-[#1A1A18] mb-4">
            {m.invalidOrExpired}
          </h1>
          <Link
            to="/booking"
            className="btn-cta inline-flex items-center gap-2 mt-4"
            data-testid="manage-invalid-cta"
          >
            <ArrowLeft size={14} />
            {m.backToBooking}
          </Link>
        </div>
      </div>
    );
  }

  if (state === 'loading' || !data) {
    return (
      <div className="min-h-screen bg-[#F5F0E8] pt-32 flex items-center justify-center">
        <span className="eyebrow text-[#8A8780]">…</span>
      </div>
    );
  }

  const intro = m.managePageIntro.replace('{name}', data.customer_name || '');

  return (
    <div className="min-h-screen bg-[#F5F0E8] pt-24 lg:pt-32 pb-24" data-testid="manage-page">
      <section className="max-w-4xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex items-center gap-4 mb-5">
            <div className="deco-line" />
            <span className="eyebrow text-[#8A8780]">{m.eyebrow}</span>
          </div>
          <h1 className="font-display text-4xl lg:text-6xl text-[#1A1A18] leading-[1.05] mb-3">
            {m.managePageTitle}
          </h1>
          <p className="font-display italic text-base lg:text-xl text-[#1A1A18]/55 max-w-2xl">
            {intro}
          </p>
        </motion.div>

        {data.bookings.length === 0 ? (
          <div
            className="border border-[#8A8780]/25 bg-white p-8 lg:p-12 text-center"
            data-testid="manage-empty"
          >
            <p className="font-display italic text-lg text-[#1A1A18]/70 mb-6">
              {m.noBookings}
            </p>
            <Link to="/booking" className="btn-cta inline-flex items-center gap-2">
              {m.bookNow}
            </Link>
          </div>
        ) : (
          <div className="space-y-5" data-testid="manage-bookings-list">
            {data.bookings.map((b) => (
              <motion.article
                key={b.booking_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="relative bg-white border border-[#8A8780]/25 overflow-hidden"
                data-testid={`manage-booking-${b.booking_id}`}
              >
                <span
                  aria-hidden
                  className="absolute left-0 top-0 h-full w-[3px] bg-[#A14223]"
                />
                <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                  <div className="lg:col-span-8">
                    <span className="eyebrow text-[#8A8780] text-[10px] flex items-center gap-2 mb-3">
                      <Calendar size={12} />
                      {formatWhen(b.start_at, language)}
                    </span>
                    <h2 className="font-display text-2xl lg:text-3xl text-[#1A1A18] tracking-tight mb-2 leading-tight">
                      {b.service_name}
                    </h2>
                    <p className="font-body text-sm text-[#1A1A18]/65 flex items-center gap-2">
                      <User size={13} className="text-[#A14223]" />
                      {m.with} <strong className="font-display not-italic text-[#1A1A18]">{b.team_member_name}</strong>
                    </p>
                  </div>
                  <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
                    {b.service_variation_id && (
                      <button
                        onClick={() => openReschedule(b)}
                        className="btn-cta text-center"
                        data-testid={`reschedule-btn-${b.booking_id}`}
                      >
                        {m.reschedule}
                      </button>
                    )}
                    <button
                      onClick={() => setCancelTarget(b)}
                      className="btn-outline text-center"
                      data-testid={`cancel-btn-${b.booking_id}`}
                    >
                      {m.cancel}
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}

        <div className="mt-12 text-center">
          <Link
            to="/booking"
            className="inline-flex items-center gap-2 eyebrow text-[#8A8780] hover:text-[#1A1A18] transition-colors"
            data-testid="manage-back-link"
          >
            <ArrowLeft size={12} />
            {m.backToBooking}
          </Link>
        </div>
      </section>

      {/* Cancel confirm dialog */}
      <AnimatePresence>
        {cancelTarget && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-[#1A1A18]/70 z-40"
              onClick={() => setCancelTarget(null)}
            />
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.96 }}
              className="fixed inset-0 z-50 flex items-center justify-center px-4"
              data-testid="cancel-confirm-modal"
            >
              <div className="relative bg-[#F5F0E8] max-w-md w-full p-8 border border-[#8A8780]/25">
                <button
                  onClick={() => setCancelTarget(null)}
                  className="absolute top-4 right-4 text-[#8A8780] hover:text-[#1A1A18]"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
                <Clock size={22} className="text-[#A14223] mb-4" />
                <h2 className="font-display text-2xl text-[#1A1A18] mb-2 leading-tight">
                  {m.cancelConfirmTitle}
                </h2>
                <p className="font-body text-sm text-[#1A1A18]/70 mb-6 leading-relaxed">
                  {m.cancelConfirmBody}
                </p>
                <p className="font-display text-base text-[#1A1A18] mb-1">
                  {cancelTarget.service_name} · {m.with} {cancelTarget.team_member_name}
                </p>
                <p className="font-body text-sm text-[#1A1A18]/65 mb-6">
                  {formatWhen(cancelTarget.start_at, language)}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => onCancel(cancelTarget)}
                    className="btn-cta flex-1"
                    data-testid="cancel-confirm-yes"
                  >
                    {m.cancelConfirmCta}
                  </button>
                  <button
                    onClick={() => setCancelTarget(null)}
                    className="btn-outline flex-1"
                    data-testid="cancel-confirm-no"
                  >
                    {m.keepIt}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Reschedule modal */}
      <AnimatePresence>
        {reschedTarget && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-[#1A1A18]/70 z-40"
              onClick={() => setReschedTarget(null)}
            />
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.96 }}
              className="fixed inset-0 z-50 flex items-center justify-center px-4"
              data-testid="reschedule-modal"
            >
              <div className="relative bg-[#F5F0E8] max-w-lg w-full p-8 border border-[#8A8780]/25 max-h-[85vh] overflow-y-auto">
                <button
                  onClick={() => setReschedTarget(null)}
                  className="absolute top-4 right-4 text-[#8A8780] hover:text-[#1A1A18]"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
                <Calendar size={22} className="text-[#A14223] mb-4" />
                <h2 className="font-display text-2xl text-[#1A1A18] mb-1 leading-tight">
                  {m.rescheduleTitle}
                </h2>
                <p className="font-display text-base text-[#1A1A18] mb-1">
                  {reschedTarget.service_name} · {m.with} {reschedTarget.team_member_name}
                </p>
                <p className="font-body text-sm text-[#1A1A18]/65 mb-5">
                  {formatWhen(reschedTarget.start_at, language)}
                </p>

                {reschedLoading ? (
                  <p className="font-body text-sm text-[#1A1A18]/60 py-6 text-center">…</p>
                ) : Object.keys(reschedSlots).length === 0 ? (
                  <p
                    className="font-body text-sm text-[#1A1A18]/70 py-4"
                    data-testid="reschedule-no-slots"
                  >
                    {m.rescheduleNoSlots}
                  </p>
                ) : (
                  <>
                    {/* Day picker */}
                    <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
                      {Object.keys(reschedSlots)
                        .sort()
                        .map((d) => (
                          <button
                            key={d}
                            onClick={() => {
                              setReschedDate(d);
                              setReschedSlot(null);
                            }}
                            className={`px-3 py-2 border whitespace-nowrap font-body text-xs transition-colors ${
                              reschedDate === d
                                ? 'bg-[#A14223] text-[#F5F0E8] border-[#A14223]'
                                : 'bg-white border-[#8A8780]/30 text-[#1A1A18] hover:border-[#A14223]'
                            }`}
                            data-testid={`reschedule-day-${d}`}
                          >
                            {formatSlotDay(d, language)}
                          </button>
                        ))}
                    </div>
                    {/* Time picker */}
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-6">
                      {(reschedSlots[reschedDate] || []).map((s) => {
                        const active =
                          reschedSlot?.start_at === s.start_at &&
                          reschedSlot?.team_member_id === s.team_member_id;
                        return (
                          <button
                            key={`${s.start_at}-${s.team_member_id}`}
                            onClick={() => setReschedSlot(s)}
                            className={`px-2 py-2.5 border text-center font-body text-sm transition-colors ${
                              active
                                ? 'bg-[#A14223] text-[#F5F0E8] border-[#A14223]'
                                : 'bg-white border-[#8A8780]/30 text-[#1A1A18] hover:border-[#A14223]'
                            }`}
                            data-testid={`reschedule-slot-${s.start_at}`}
                          >
                            {formatSlotTime(s.start_at, language)}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={onReschedule}
                        disabled={!reschedSlot || reschedSubmitting}
                        className="btn-cta flex-1 disabled:opacity-40"
                        data-testid="reschedule-confirm"
                      >
                        {m.rescheduleCta}
                      </button>
                      <button
                        onClick={() => setReschedTarget(null)}
                        className="btn-outline flex-1"
                        data-testid="reschedule-cancel"
                      >
                        {m.keepIt}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onAnimationComplete={() => setTimeout(() => setToast(null), 3000)}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#25342C] text-[#F5F0E8] px-6 py-3 flex items-center gap-3 shadow-lg"
            data-testid="manage-toast"
          >
            <CheckCircle2 size={16} className="text-[#A14223]" />
            <span className="font-body text-sm">{toast}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ManageBookingPage;
